import { computed, ref } from 'vue'
import type { ComputedRef, Ref } from 'vue'

export type UseLocalWishlistReturn = {
    /**
     * Get wishlist products from localstorage
     */
    getWishlistProducts(): void
    /**
     * Add product to wishlist by its id, optionally with its raw Excentos configurator
     * payload (JSON string)
     */
    addToWishlist(id: string, config?: string): Promise<void>
    /**
     * Remove product from wishlist by its id, dropping its stored config if any
     */
    removeFromWishlist(id: string): Promise<void>
    /**
     * Remove all products from wishlist
     */
    clearWishlist(): Promise<void>
    /**
     * Get the stored Excentos config (JSON string) for a wishlisted product, if any
     */
    getConfig(id: string): string | undefined
    /**
     * List of wishlist items
     */
    items: ComputedRef<string[]>
    /**
     * Count of wishlist items
     */
    count: ComputedRef<number>
    /**
     * The stored id→config map, omitting ids with no config — the shape
     * `useSyncWishlist.mergeWishlistProducts` and the wishlist product list expect
     */
    configs: ComputedRef<Record<string, string>>
}

const STORAGE_KEY = 'sw-wishlist-items'

// `null`, not `undefined` — `JSON.stringify` drops object keys whose value is `undefined`,
// which would silently lose unconfigured items (the common case) on the next serialization.
const _wishlistItems: Ref<Record<string, string | null>> = ref({})

/**
 * Override of the upstream `useLocalWishlist`, which stores a plain `string[]` of product
 * ids. Excentos-configured products need their configuration to survive as a guest and
 * through login-merge, so this stores an id→config map (config optional per id) under the
 * same localStorage key instead.
 */
export function useLocalWishlist(): UseLocalWishlistReturn {
    const updateStorage = (): void => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(_wishlistItems.value))
    }

    const getFromStorage = (): unknown => {
        try {
            return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}')
        } catch {
            return undefined
        }
    }

    async function removeFromWishlist(id: string) {
        _wishlistItems.value = Object.fromEntries(
            Object.entries(_wishlistItems.value).filter(([itemId]) => itemId !== id),
        )

        updateStorage()
    }

    async function addToWishlist(id: string, config?: string) {
        if (!(id in _wishlistItems.value)) {
            _wishlistItems.value = { ..._wishlistItems.value, [id]: config ?? null }

            updateStorage()
        }
    }

    async function clearWishlist() {
        _wishlistItems.value = {}
        updateStorage()
    }

    function getWishlistProducts() {
        const stored = getFromStorage()

        if (Array.isArray(stored)) {
            // storage shape: plain array of ids, no configs.
            if (stored.length) {
                _wishlistItems.value = Object.fromEntries(stored.map((id: string) => [id, null]))
            }
        } else if (stored != null && typeof stored === 'object' && Object.keys(stored).length) {
            _wishlistItems.value = stored as Record<string, string | null>
        }
    }

    function getConfig(id: string): string | undefined {
        return _wishlistItems.value[id] ?? undefined
    }

    const items = computed(() => Object.keys(_wishlistItems.value))
    const count = computed(() => items.value.length)
    const configs = computed(() => Object.fromEntries(
        Object.entries(_wishlistItems.value).filter((entry): entry is [string, string] => entry[1] != null),
    ))

    return {
        getWishlistProducts,
        addToWishlist,
        removeFromWishlist,
        clearWishlist,
        getConfig,
        items,
        count,
        configs,
    }
}
